import * as React from 'react';
import { TextField, TextFieldProps } from '@mui/material';

type NumericInputFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number;
  onChange: (value: number) => void;
  emptyValue?: number;
};

export const NumericInputField = ({
  value,
  onChange,
  emptyValue = 0,
  onBlur,
  onFocus,
  inputProps,
  ...props
}: NumericInputFieldProps) => {

  const [inputValue, setInputValue] = React.useState(() => String(value));
  const isFocusedRef = React.useRef(false);

  React.useEffect(() => {

    if (!isFocusedRef.current) {

      setInputValue(String(value));

    }

  }, [value]);

  const commitValue = (raw: string) => {

    if (raw === '' || raw === '-') {

      onChange(emptyValue);
      setInputValue(String(emptyValue));
      return;

    }

    const parsed = Number(raw);
    if (Number.isNaN(parsed)) {

      setInputValue(String(value));
      return;

    }

    onChange(parsed);
    setInputValue(String(parsed));

  };

  return (
    <TextField
      {...props}
      type="text"
      inputMode="decimal"
      value={inputValue}
      inputProps={inputProps}
      onFocus={(event) => {

        isFocusedRef.current = true;
        onFocus?.(event);

      }}
      onBlur={(event) => {

        isFocusedRef.current = false;
        commitValue(inputValue);
        onBlur?.(event);

      }}
      onChange={(event) => setInputValue(event.target.value)}
    />
  );

};

type NumericTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (value: string) => void;
};

export const NumericTextField = ({
  value,
  onChange,
  onBlur,
  onFocus,
  inputProps,
  ...props
}: NumericTextFieldProps) => {

  const [inputValue, setInputValue] = React.useState(value);
  const isFocusedRef = React.useRef(false);

  React.useEffect(() => {

    if (!isFocusedRef.current) {

      setInputValue(value);

    }

  }, [value]);

  return (
    <TextField
      {...props}
      type="text"
      inputMode="decimal"
      value={inputValue}
      inputProps={inputProps}
      onFocus={(event) => {

        isFocusedRef.current = true;
        onFocus?.(event);

      }}
      onBlur={(event) => {

        isFocusedRef.current = false;
        onChange(inputValue);
        onBlur?.(event);

      }}
      onChange={(event) => {

        const nextValue = event.target.value;
        setInputValue(nextValue);
        onChange(nextValue);

      }}
    />
  );

};
